<template>
  <b-card>
    <base-overlay :show="fetching">
      <b-table
        :items="formattedList"
        :fields="[
          { key: 'showDetails', label: '' },
          { key: 'pickup', label: 'Pickup Location' }, { key: 'actions', label: 'Actions' }]"
        responsive
        show-empty
        empty-text="No matching records found"
      >
        <template #cell(showDetails)="row">
          <b-form-checkbox
            v-model="row.detailsShowing"
            @change="showDetails(row)"
          >
            <span>{{ row.detailsShowing ? 'Hide' : 'Show' }} details</span>
          </b-form-checkbox>
        </template>

        <!-- Pickup Cell -->
        <template #cell(pickup)="data">
          {{ data.item.pickup }}
        </template>

        <!-- Actions -->
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="warning"
            @click="openEditModal(data.item)"
          >
            Edit
          </b-button>
        </template>

        <!-- Nested Dropoffs Table -->
        <template #row-details="data">
          <b-table
            :items="data.item.dropoffs"
            :fields="nestedFields"
            small
            responsive
          >
            <template #cell(name)="drop">
              {{ drop.item.name }}
            </template>

            <!-- Dynamic service columns -->
            <template
              v-for="(service, ix) in serviceNames"
              #[`cell(${service})`]="drop"
            >
              <div :key="ix">
                <div
                  v-for="(s, i) in drop.item.services[service] || []"
                  :key="i"
                >
                  <strong>{{ s.service }}:</strong> {{ s.priceRange }}
                </div>
              </div>
            </template>
          </b-table>
        </template>
      </b-table>
      <b-pagination
        v-if="pagination.totalPages > 1"
        v-model="pagination.page"
        :total-rows="pagination.totalItems"
        :per-page="pagination.perPage"
        class="mt-3"
        @input="fetch"
      />
    </base-overlay>

    <!-- Edit Modal -->
    <b-modal
      v-model="showModal"
      title="Edit Coordinates"
      size="xl"
      @hide="resetEditForm"
    >
      <div>
        <h5 class="mb-3">
          Pickup
        </h5>
        <b-row>
          <b-col cols="12">
            <b-form-group label="Pickup Name">
              <b-form-input v-model="editForm.pickup.name" />
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label="Pickup Latitude">
              <b-form-input
                v-model.number="editForm.pickup.lat"
                type="number"
              />
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label="Pickup Longitude">
              <b-form-input
                v-model.number="editForm.pickup.lng"
                type="number"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <hr class="my-4">

        <!-- Dropoffs Fields in 6-columns layout -->
        <h5 class="mb-3">
          Dropoffs
        </h5>
        <b-row
          v-for="(drop, index) in editForm.dropoffs"
          :key="index"
          class="mb-3"
        >
          <b-col cols="6">
            <b-form-group :label="`Dropoff ${index + 1} Name`">
              <b-form-input v-model="drop.name" />
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group :label="`Latitude`">
              <b-form-input
                v-model.number="drop.lat"
                type="number"
              />
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group :label="`Longitude`">
              <b-form-input
                v-model.number="drop.lng"
                type="number"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>

      <!-- Modal Footer -->
      <template #modal-footer>
        <b-button
          variant="secondary"
          @click="showModal = false"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="!isFormValid || submittingEdit"
          @click="submitEdit"
        >
          <b-spinner
            v-if="submittingEdit"
            small
          />
          Save
        </b-button>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BTable,
  BCard,
  BPagination,
  BModal,
  BButton,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BRow,
  BCol,
  BSpinner,
} from 'bootstrap-vue'
import { ref, computed } from '@vue/composition-api'
import axiosIns from '@/libs/axios'

export default {
  components: {
    BTable,
    BCard,
    BModal,
    BButton,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BPagination,
    BFormCheckbox,
    BSpinner,
  },
  setup() {
    const fetching = ref(false)
    const list = ref([])
    const pagination = ref({
      page: 1,
      perPage: 5,
      totalItems: 0,
      totalPages: 0,
    })

    const showModal = ref(false)
    const editForm = ref({
      itemId: null,
      pickup: { name: '', lat: null, lng: null },
      dropoffs: [],
    })

    const formattedList = computed(() => {
      const map = new Map()

      list.value.forEach(item => {
        const {
          itemId, pickup, dropoff, etaResult,
        } = item

        if (!map.has(itemId)) {
          map.set(itemId, {
            itemId,
            pickup: pickup.name.trim(),
            pickupCoord: { ...pickup },
            dropoffs: [],
          })
        }

        const group = map.get(itemId)

        let existingDropoff = group.dropoffs.find(d => d.name === dropoff.name.trim())

        if (!existingDropoff) {
          existingDropoff = {
            name: dropoff.name.trim(),
            coord: { ...dropoff },
            services: {},
          }
          group.dropoffs.push(existingDropoff)
        }

        // eslint-disable-next-line no-unused-expressions
        etaResult.compArray?.forEach(comp => {
          if (!existingDropoff.services[comp.name]) {
            existingDropoff.services[comp.name] = []
          }

          // eslint-disable-next-line no-unused-expressions
          comp.estimates?.forEach(estimate => {
            existingDropoff.services[comp.name].push({
              service: estimate.service,
              priceRange: estimate.priceRange,
            })
          })
        })
      })

      return Array.from(map.values())
    })

    const serviceNames = computed(() => {
      const services = new Set()
      list.value.forEach(item => {
        item.etaResult.compArray.forEach(comp => services.add(comp.name))
      })
      return Array.from(services)
    })

    const nestedFields = computed(() => [
      { key: 'name', label: 'Dropoff' },
      ...serviceNames.value.map(s => ({ key: s, label: s })),
    ])

    const fetch = async () => {
      fetching.value = true
      try {
        const res = await axiosIns.get('/comp-eta/configured', {
          params: {
            page: pagination.value.page,
            perPage: pagination.value.perPage,
          },
        })
        const { results, totalItems, totalPages } = res.data
        list.value = results
        pagination.value.totalItems = totalItems
        pagination.value.totalPages = totalPages
      } catch (err) {
        console.error('Fetch error:', err)
      } finally {
        fetching.value = false
      }
    }
    const openEditModal = item => {
      editForm.value = {
        itemId: item.itemId,
        pickup: { name: item.pickup, ...item.pickupCoord },
        dropoffs: item.dropoffs.map(d => ({
          name: d.name,
          lat: d.coord.lat,
          lng: d.coord.lng,
        })),
      }
      showModal.value = true
    }

    const resetEditForm = () => {
      editForm.value = {
        itemId: null,
        pickup: { lat: null, lng: null },
        dropoff: { lat: null, lng: null },
      }
    }

    const submittingEdit = ref(false)
    const submitEdit = async () => {
      try {
        submittingEdit.value = true
        const payload = {
          pickup: editForm.value.pickup,
          dropoffs: editForm.value.dropoffs,
        }

        await axiosIns.put(`/comp-eta/items/${editForm.value.itemId}`, payload)

        showModal.value = false
        fetch()
      } catch (e) {
        console.error('Update failed', e)
      } finally {
        submittingEdit.value = false
      }
    }
    const showDetails = async row => {
      row.toggleDetails()
    }

    const getItemServices = (drop, service) => drop.item.services[service]?.priceRange || '-'

    const isFormValid = computed(() => {
      const p = editForm.value.pickup
      if (!p.name || !p.lat || !p.lng) return false
      return editForm.value.dropoffs.every(
        d => d.name && d.lat && d.lng,
      )
    })

    fetch()

    return {
      isFormValid,
      getItemServices,
      showDetails,
      fetching,
      list,
      formattedList,
      pagination,
      fetch,
      showModal,
      submittingEdit,
      editForm,
      serviceNames,
      openEditModal,
      submitEdit,
      resetEditForm,
      nestedFields,
    }
  },
}
</script>
